import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

type DateRange = {
  startDate: Date;
  endDate: Date;
};

type ValidFilterState = {
  type: 'valid';
  dateRange: DateRange;
  searchTerm: string | undefined;
};

type InvalidFilterState = {
  type: 'invalid';
  dateRange: DateRange | null;
  searchTerm: string | undefined;
  validationMessage: string;
};

type PaginationState = {
  pageSize: number;
  pageIndex: number;
};

type FilterState = ValidFilterState | InvalidFilterState;

type ActionType =
  | { type: 'SET_DATE_RANGE'; payload: DateRange }
  | { type: 'SET_SEARCH_TERM'; payload: string }
  | { type: 'SET_LAST_REFETCH_DATE'; payload: Date }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'RESET_FILTERS' };

interface ProfilesStore {
  filters: FilterState;
  pagination: PaginationState;
  refetchLastDate: Date | null;
  dispatch: (action: ActionType) => void;
}

const initialState: Omit<ProfilesStore, 'dispatch'> = {
  filters: {
    type: 'invalid',
    dateRange: null,
    searchTerm: '',
    validationMessage: 'no filters set'
  },
  pagination: {
    pageSize: 10,
    pageIndex: 1
  },
  refetchLastDate: null
};

const validateFilters = (filters: InvalidFilterState | ValidFilterState): FilterState => {
  if (!filters.dateRange) {
    return {
      type: 'invalid',
      dateRange: null,
      searchTerm: filters.searchTerm,
      validationMessage: 'No date range set'
    };
  }

  if (filters.dateRange.startDate > filters.dateRange.endDate) {
    return {
      type: 'invalid',
      dateRange: filters.dateRange,
      searchTerm: filters.searchTerm,
      validationMessage: 'Start date must be before end date'
    };
  }

  return {
    type: 'valid',
    dateRange: filters.dateRange,
    searchTerm: filters.searchTerm
  };
};

const profilesStore = create<ProfilesStore>()(
  devtools(
    immer((set) => ({
      ...initialState,
      dispatch: (action: ActionType) => {
        set((state) => {
          switch (action.type) {
            case 'SET_DATE_RANGE': {
              const newState: InvalidFilterState = {
                type: 'invalid',
                dateRange: action.payload,
                searchTerm: state.filters.searchTerm,
                validationMessage:
                  state.filters.type === 'invalid'
                    ? state.filters.validationMessage
                    : 'Validating...'
              };
              state.filters = validateFilters(newState);
              break;
            }
            case 'SET_SEARCH_TERM': {
              const newState: InvalidFilterState = {
                type: 'invalid',
                dateRange: state.filters.dateRange,
                searchTerm: action.payload,
                validationMessage:
                  state.filters.type === 'invalid'
                    ? state.filters.validationMessage
                    : 'Validating...'
              };
              state.filters = validateFilters(newState);
              break;
            }
            case 'SET_LAST_REFETCH_DATE':
              state.refetchLastDate = action.payload;
              break;
            case 'RESET_FILTERS':
              state.filters = initialState.filters;
              break;
            case 'SET_PAGE':
              state.pagination = {
                pageSize: state.pagination.pageSize,
                pageIndex: action.payload
              };
              break;
          }
        });
      }
    }))
  )
);

// Action creators
export const createActions = () => {
  const dispatch = profilesStore.getState().dispatch;
  return {
    setDateRange: (dateRange: DateRange) => {
      dispatch({ type: 'SET_DATE_RANGE', payload: dateRange });
    },
    setSearchTerm: (searchTerm: string) => {
      dispatch({ type: 'SET_SEARCH_TERM', payload: searchTerm });
    },
    setLastRefetchDate: (lastRefetchDate: Date) => {
      dispatch({ type: 'SET_LAST_REFETCH_DATE', payload: lastRefetchDate });
    },
    setPage: (page: number) => {
      dispatch({ type: 'SET_PAGE', payload: page });
    },
    resetFilters: () => {
      dispatch({ type: 'RESET_FILTERS' });
    }
  };
};

// Helper hooks
export const useProfileFilters = () => profilesStore((state) => state.filters);
export const useRefetchLastDate = () => profilesStore((state) => state.refetchLastDate);
export const useProfileActions = () => createActions();

// Helper selectors
export const useFilterDateRange = () => profilesStore((state) => state.filters.dateRange);
export const useFilterSearchTerm = () => profilesStore((state) => state.filters.searchTerm);
export const useFilterPage = () => profilesStore((state) => state.pagination);
export const useFilterValidationMessage = () => {
  const filters = useProfileFilters();
  return filters.type === 'invalid' ? filters.validationMessage : null;
};
export const useIsFilterValid = () => {
  const filters = useProfileFilters();
  return filters.type === 'valid';
};
